import React from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/700.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { HelmetProvider } from 'react-helmet-async'

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb', // Primary from CSS
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#10b981', // Secondary from CSS
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Light from CSS
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b', // Dark from CSS
      secondary: '#64748b', // Gray from CSS
    },
  },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    h1: { fontSize: '3.5rem', fontWeight: 800 },
    h2: { fontSize: '2.5rem', fontWeight: 700 },
    h3: { fontSize: '1.5rem', fontWeight: 600 },
    subtitle1: { fontSize: '1.25rem', color: '#64748b' },
    button: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f8fafc', // Light background
          color: '#1e293b',
          lineHeight: 1.6,
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
          transition: 'all 0.3s ease',
          '&:hover': { color: '#2563eb' },
        },
        '.btn-primary': {
          backgroundColor: '#2563eb',
          color: '#ffffff',
          borderRadius: '0.375rem',
          fontWeight: 600,
          padding: '0.75rem 1.5rem',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: '#1d4ed8',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        },
        '.btn-secondary': {
          backgroundColor: '#10b981',
          color: '#ffffff',
          borderRadius: '0.375rem',
          fontWeight: 600,
          padding: '0.75rem 1.5rem',
          transition: 'all 0.3s ease',
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        },
        '.btn-outline': {
          border: '2px solid #2563eb',
          color: '#2563eb',
          background: 'transparent',
          '&:hover': {
            backgroundColor: '#2563eb',
            color: '#ffffff',
          },
        },
      },
    },
  },
})

Sentry.init({
  dsn: 'https://02468f8c0315c9d26143ffc94e8231c1@o4508783793995776.ingest.de.sentry.io/4508783796682832',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </I18nextProvider>
    </HelmetProvider>
  </React.StrictMode>
)

reportWebVitals()
